/**
Adapted from
https://github.com/svecosystem/runed/blob/main/packages/runed/src/lib/utilities/element-rect/element-rect.svelte.ts
MIT License:
https://github.com/svecosystem/runed/blob/main/LICENSE
*/
import { extract } from '@vpap_svelte/internal';
import { useMutationObserver } from '../useMutationObserver/useMutationObserver.svelte.js';
import { useResizeObserver, } from '../useResizeObserver/useResizeObserver.svelte.js';
/**
Returns a reactive value holding the size of the `node`.

Accepts an `options` object with the following properties:
- `initialSize`: The initial size of the element. Defaults to `width: 0, height: 0`
- `box`: The box model to use (either `"content-box"`` or `"border-box"`). Defaults to `"border-box"`

@returns an object with `width` and `height` properties
 `*/
export class ElementRect {
    #rect = $state({
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    });
    #isInitial = $state(true);
    constructor(node, options = {}) {
        this.#rect = {
            width: options.initialRect?.width ?? 0,
            height: options.initialRect?.height ?? 0,
            x: options.initialRect?.x ?? 0,
            y: options.initialRect?.y ?? 0,
            top: options.initialRect?.top ?? 0,
            right: options.initialRect?.right ?? 0,
            bottom: options.initialRect?.bottom ?? 0,
            left: options.initialRect?.left ?? 0,
        };
        const el = $derived(extract(node));
        const update = () => {
            if (!el)
                return;
            const rect = el.getBoundingClientRect();
            this.#rect.width = rect.width;
            this.#rect.height = rect.height;
            this.#rect.x = rect.x;
            this.#rect.y = rect.y;
            this.#rect.top = rect.top;
            this.#rect.right = rect.right;
            this.#rect.bottom = rect.bottom;
            this.#rect.left = rect.left;
            this.#isInitial = false;
        };
        useResizeObserver(() => el, update, options.resizeOptions);
        $effect(update);
        useMutationObserver(() => el, update, {
            attributeFilter: ['style', 'class'],
        });
    }
    get x() {
        return this.#rect.x;
    }
    get y() {
        return this.#rect.y;
    }
    get width() {
        return this.#rect.width;
    }
    get height() {
        return this.#rect.height;
    }
    get top() {
        return this.#rect.top;
    }
    get right() {
        return this.#rect.right;
    }
    get bottom() {
        return this.#rect.bottom;
    }
    get left() {
        return this.#rect.left;
    }
    get current() {
        return this.#rect;
    }
    /**
     * Whether the rect is simply its initial, unset, value (as it is during SSR or before the `update`
     * effect has been run.)
     */
    get isInitial() {
        return this.#isInitial;
    }
}
